import type { CodeScannerState } from '@cli-types/core-engine/utils.js'

export function createCodeScannerState(): CodeScannerState {
  return {
    inString: null,
    inLineComment: false,
    inBlockComment: false
  }
}

/**
 * Walks one character at a time, reporting whether the position sits inside a
 * string or comment so a caller can skip it. Delimiter matching over JavaScript
 * source is wrong without this: a brace inside a string literal or a commented
 * -out block otherwise counts toward depth.
 */
export function advanceCodeScanner(
  content: string,
  index: number,
  state: CodeScannerState
): { skip: true; nextIndex: number } | { skip: false; nextIndex: number } {
  const char = content[index]
  const next = content[index + 1]
  const prev = content[index - 1]

  if (state.inLineComment) {
    if (char === '\n') state.inLineComment = false
    return { skip: true, nextIndex: index }
  }

  if (state.inBlockComment) {
    if (prev === '*' && char === '/') state.inBlockComment = false
    return { skip: true, nextIndex: index }
  }

  if (state.inString) {
    if (char === state.inString && prev !== '\\') state.inString = null
    return { skip: true, nextIndex: index }
  }

  if (char === '/' && next === '/') {
    state.inLineComment = true
    return { skip: true, nextIndex: index + 1 }
  }

  if (char === '/' && next === '*') {
    state.inBlockComment = true
    return { skip: true, nextIndex: index + 1 }
  }

  if (char === '"' || char === "'" || char === '`') {
    state.inString = char
    return { skip: true, nextIndex: index }
  }

  return { skip: false, nextIndex: index }
}

export function findMatchingDelimiter(
  content: string,
  openingIndex: number,
  openingDelimiter: string,
  closingDelimiter: string
): number {
  let depth = 0
  const scannerState = createCodeScannerState()

  for (let i = openingIndex; i < content.length; i++) {
    const scanResult = advanceCodeScanner(content, i, scannerState)
    if (scanResult.skip) {
      i = scanResult.nextIndex
      continue
    }

    const char = content[i]

    if (char === openingDelimiter) {
      depth++
      continue
    }

    if (char === closingDelimiter) {
      depth--
      if (depth === 0) return i
    }
  }

  return -1
}

export function findMatchingBrace(content: string, openingBrace: number): number {
  return findMatchingDelimiter(content, openingBrace, '{', '}')
}

export function findMatchingBracket(content: string, openingBracket: number): number {
  return findMatchingDelimiter(content, openingBracket, '[', ']')
}

export function isIdentifierStart(char: string | undefined): boolean {
  return !!char && /[A-Za-z_$]/.test(char)
}

export function isIdentifierBoundary(char: string | undefined): boolean {
  return !char || !/[A-Za-z0-9_$]/.test(char)
}

export function getLineIndent(content: string, position: number): string {
  const lineStart = content.lastIndexOf('\n', position) + 1
  return content.slice(lineStart, position).match(/^\s*/)?.[0] ?? ''
}
